import React from 'react';
import styled, { css } from 'styled-components';
import loadable from '@loadable/component';
import { useDispatch } from 'react-redux';
import { caption1 } from '@westwing/ui-kit/typography';
import { forDesktop, forIE } from '@westwing/ui-kit/breakPoints';
import HybridLink from '../../Generic/Links/HybridLink';
import { OverlayId } from '../../Overlays/types';
import { showOverlay, hideOverlay } from '../../../redux/overlays/actions';
import { VictorQaClasses } from '../../../../common/qaIds';
import HelpCenterContent from './HelpCenterContent';
import { useIsLoggedIn } from 'Client/hooks/useIsLoggedIn';
import useTranslator from 'Client/hooks/useTranslator';
import useConfig from 'Client/hooks/useConfig';

const ContentOverlay = loadable(() => import('../../Overlays/ContentOverlay'));

const InfoBar = styled.div`
    grid-area: info;
    align-items: center;
    align-content: center;
    display: none;
    grid-template-columns: repeat(3, auto);
    grid-template-rows: 30px;
    justify-content: end;
    padding-right: 20px;
    width: 100%;
    background: ${({ theme }) => theme.colors.turquoise100};
    color: ${({ theme }) => theme.colors.charcoal100};

    ${forDesktop`
        display: grid;
    `}

    ${forIE`
        -ms-grid-row: 1;
        -ms-grid-column: 7;
        -ms-grid-column-span: 2;
        display: flex;
    `}
`;

const CommonTextStyles = css`
    ${caption1}
    padding: 0 20px;
    position: relative;

    &::before {
        background: ${({ theme }) => theme.colors.charcoal100};
        content: '';
        position: absolute;
        height: 10px;
        top: 3px;
        left: 0;
        width: 1px;
    }

    &:first-child {
        &::before {
            display: none;
        }
    }
`;

const StyledInfoLink = styled(HybridLink)`
    text-decoration: none;
`;

const HelpCenterLink = styled(HybridLink)`
    color: ${({ theme }) => theme.colors.charcoal100};
    font-family: ${({ theme }) => theme.fontBrandonRegular};
    :hover {
        text-decoration: underline;
    }
`;

const HelpCenterWrapper = styled.div`
    ${caption1}
    ${CommonTextStyles}
    cursor: pointer;
`;

const DiscountPromotionLink = styled(StyledInfoLink)`
    ${caption1}
    position: relative;
    color: ${({ theme }) => theme.colors.charcoal100};
    padding: 1px 20px 0;
    :hover {
        text-decoration: underline;
    }
`;

const FreeShippingText = styled.span`
    ${CommonTextStyles}
`;

const InfoSection: React.FC<{
    minOrderValue: number | null;
}> = ({ minOrderValue }) => {
    const t = useTranslator();
    const dispatch = useDispatch();
    const isUserLoggedIn = useIsLoggedIn();
    const {
        links: { clubBaseUrl, clubReferAFriendPath, helpCenterUrl },
    } = useConfig();

    const clubReferAFriendURL = `${clubBaseUrl}${clubReferAFriendPath}`;
    const openHelpCenter = () => dispatch(showOverlay(OverlayId.DESKTOP_HELP_CENTER_OVERLAY));
    const closeHelpCenter = () => dispatch(hideOverlay(OverlayId.DESKTOP_HELP_CENTER_OVERLAY));

    return (
        <InfoBar onMouseLeave={closeHelpCenter}>
            {isUserLoggedIn && (
                <DiscountPromotionLink to={clubReferAFriendURL}>{t('Refer_a_friend_menu_item')}</DiscountPromotionLink>
            )}
            {minOrderValue !== null && (
                <FreeShippingText data-testid="free-ship">
                    {t('Free shipping from %s', [minOrderValue])}
                </FreeShippingText>
            )}
            <HelpCenterWrapper>
                <HelpCenterLink
                    to={helpCenterUrl}
                    onMouseEnter={openHelpCenter}
                    className={VictorQaClasses.headerHelpCenterLink}
                >
                    {t('Customer_care_FAQ_link')}
                </HelpCenterLink>
                <ContentOverlay
                    identifier={OverlayId.DESKTOP_HELP_CENTER_OVERLAY}
                    rightPosition={0}
                    propagateOutsideClicks
                >
                    <HelpCenterContent />
                </ContentOverlay>
            </HelpCenterWrapper>
        </InfoBar>
    );
};

export default InfoSection;
